import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api, type User } from '@/services/api'
import { useAppStore } from './useAppStore'
import { GetuAttributionService } from '@/services/attribution'

interface AuthStore {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  isDevMode: boolean

  // Actions
  logout: () => void
  fetchCurrentUser: () => Promise<void>
  refreshCredits: () => Promise<void>
  setAuthenticated: (authenticated: boolean) => void
  setDevMode: () => void
  clearAuth: () => void
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      isDevMode: false,

      logout: () => {
        get().clearAuth()
      },

      fetchCurrentUser: async () => {
        try {
          set({ isLoading: true })
          const response = await api.auth.getMe()
          if (response.success && response.data) {
            const u = response.data
            set({
              user: u,
              isAuthenticated: true,
            })
            GetuAttributionService.setUserIdBestEffort(u.id)
            GetuAttributionService.trackLoginBestEffort(u.id, { email: u.email })
            // Set company info if available, but preserve analysisResult if already loaded
            if (response.data.company) {
              const currentCompanyInfo = useAppStore.getState().companyInfo
              // Only set company info if not already loaded, or merge to preserve analysisResult
              if (!currentCompanyInfo) {
                useAppStore.getState().setCompanyInfo(response.data.company as any)
              } else if (currentCompanyInfo.domain === (response.data.company as any).domain) {
                // Same company - preserve existing analysisResult
                useAppStore.getState().setCompanyInfo({
                  ...response.data.company,
                  analysisResult: currentCompanyInfo.analysisResult,
                } as any)
              } else {
                // Different company - need to fetch full data
                useAppStore.getState().setCompanyInfo(response.data.company as any)
              }
            } else if (!useAppStore.getState().companyInfo) {
              // Only clear if no company info exists
              useAppStore.getState().setCompanyInfo(null)
            }
          }
        } catch (error) {
          console.error('Failed to fetch current user:', error)
          // Don't clear auth on error - might be a network issue
        } finally {
          set({ isLoading: false })
        }
      },

      refreshCredits: async () => {
        const user = get().user
        if (!user) {
          return
        }

        try {
          const response = await api.credits.getBalance()
          if (response.credits_balance !== undefined) {
            set({
              user: {
                ...user,
                credits: response.credits_balance
              }
            })
          }
        } catch (error) {
          console.error('Failed to refresh credits:', error)
        }
      },

      setAuthenticated: (authenticated) => {
        set({ isAuthenticated: authenticated })
      },

      setDevMode: () => {
        // Set dev mode authenticated state
        set({
          isAuthenticated: true,
          isDevMode: true,
          user: {
            id: 'dev-user',
            username: 'developer',
            email: 'dev@localhost',
            full_name: 'Developer',
            is_active: true,
            is_superuser: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          }
        })
      },

      clearAuth: () => {
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          isDevMode: false,
        })
        // Clear company info
        useAppStore.getState().setCompanyInfo(null)
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        isDevMode: state.isDevMode,
      })
    }
  )
)

// Helper hook to check if user is authenticated
export const useIsAuthenticated = () => {
  return useAuthStore((state) => state.isAuthenticated)
}

// Helper hook to get current user
export const useCurrentUser = () => {
  return useAuthStore((state) => state.user)
}
